import React, { useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { REIButton, REIDataTable, REIDivider, REIDropDown, REIErrorMessage, REIInputText } from "../../components/ui";
import REICard from "../../components/ui/card";
import InputSlider from "../../components/ui/slider";
import REITooltip from "../../components/ui/tooltip";
import { DEPRECIATION_API_URL, REACT_APP_WB_API_TOKEN } from "../../utils/constants";
import { TypeValue } from "../../types/depreciationCalculator";
import { ICalculatorForm, ICalculatorResult, IRowData } from "../../interface/deprecitionCalculator";
import styles from "./WashingtonBrownCalculator.module.scss";

const CITY_OPTIONS = [
  { label: "Sydney NSW", value: "SYDNEY" },
  { label: "Melbourne VIC", value: "MELBOURNE" },
  { label: "Perth WA", value: "PERTH" },
  { label: "Brisbane QLD", value: "BRISBANE" },
  { label: "Adelaide SA", value: "ADELAIDE" },
  { label: "Cairns QLD", value: "CAIRNS" },
  { label: "Canberra ACT", value: "CANBERRA" },
  { label: "Darwin NT", value: "DARWIN" },
  { label: "Hobart TAS", value: "HOBART" },
];

const TYPE_OPTIONS = [
  { label: "House / Townhouse", value: "HOUSE_TOWNHOUSE" },
  { label: "Unit", value: "UNIT" },
  { label: "Highrise", value: "HIGHRISE" },
  { label: "Commercial Property", value: "COMMERCIAL_SUITE" },
  { label: "Industrial Property", value: "INDUSTRIAL_SUITE" },
];

const FINISH_OPTIONS = [
  { label: "Low", value: "LOW" },
  { label: "Medium", value: "MEDIUM" },
  { label: "High", value: "HIGH" },
];

const PURCHASE_DATE_OPTIONS = [
  { label: "Pre-Budget 9 May 2017", value: "0" },
  { label: "Post-Budget 9 May 2017 (Second Hand)", value: "1" },
  { label: "Post-Budget 9 May 2017 (Brand New)", value: "2" },
];

const YEAR_MIN = 1987;
const YEAR_MAX = new Date().getFullYear();

const toCurrency = (value: number) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const readXmlTag = (doc: Document, tagName: string) => doc.querySelector(tagName)?.textContent || "";

const mapCategory = (category: TypeValue) => {
  switch (category) {
    case "HOUSE_TOWNHOUSE":
      return "HOUSE";
    case "UNIT":
      return "UNIT";
    case "HIGHRISE":
      return "HIGHRISE";
    case "COMMERCIAL_SUITE":
      return "COMMERCIAL SUITE";
    case "INDUSTRIAL_SUITE":
      return "INDUSTRIAL SUITE";
    default:
      return category;
  }
};

const parseYears = (doc: Document, prefix: "DV" | "PC") => {
  const years: number[] = [];
  for (let i = 1; i <= 10; i += 1) {
    years.push(Number(readXmlTag(doc, `${prefix}_YEAR_${i}`) || 0));
  }
  return years;
};

const defaultTracking = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      rid: params.get("RID") || "",
      tracking: params.get("tracking") || "wbdepquote",
    };
  } catch {
    return { rid: "", tracking: "wbdepquote" };
  }
};

const DEFAULT_CALCULATOR_VALUES: Omit<ICalculatorForm, "rid" | "tracking"> = {
  price: "480000",
  pdate: "0",
  date: "2005",
  type: "HOUSE_TOWNHOUSE",
  finish: "MEDIUM",
  city: "SYDNEY",
};

const WashingtonBrownCalculator: React.FC = ({}) => {
  const apiBaseUrl = DEPRECIATION_API_URL;
  const apiToken = REACT_APP_WB_API_TOKEN;
  const trackingDefaults = useMemo(defaultTracking, []);
  const initialValues: ICalculatorForm = {
    ...DEFAULT_CALCULATOR_VALUES,
    rid: trackingDefaults.rid,
    tracking: trackingDefaults.tracking,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ICalculatorResult | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const rows: IRowData[] = useMemo(() => {
    if (!result) return [];
    const yearRows = result.dvYears.map((_, idx) => ({
      year: `Year ${idx + 1}`,
      dv: result.dvYears[idx],
      pc: result.pcYears[idx],
    }));
    return [
      ...yearRows,
      {
        year: "First 10 Year Total",
        dv: result.dvTotal,
        pc: result.pcTotal,
      },
    ];
  }, [result]);

  const totalRowClassName = (rowData: IRowData) => {
    if (rowData.year === "First 10 Year Total") {
      return "bg-cyan-100";
    }
    return "";
  };

  const getTableColumns = (showDV: boolean) => [
    {
      field: "year",
      header: "Years",
      bodyTemplate: (r: IRowData) => {
        const isTotal = r.year === "First 10 Year Total";
        return (
          <span
            className={
              isTotal
                ? "inline-flex min-h-[2.75rem] w-full items-center py-2 font-semibold text-cyan-900"
                : "text-slate-700"
            }
          >
            {r.year}
          </span>
        );
      },
    },
    ...(showDV
      ? [
          {
            field: "dv",
            header: "Diminishing Value",
            bodyTemplate: (r: IRowData) => {
              const isTotal = r.year === "First 10 Year Total";
              return (
                <span
                  className={
                    isTotal
                      ? "inline-flex min-h-[2.75rem] w-full items-center py-2 font-semibold text-cyan-900"
                      : "text-slate-700"
                  }
                >
                  {toCurrency(r.dv)}
                </span>
              );
            },
          },
        ]
      : []),
    {
      field: "pc",
      header: "Prime Cost",
      bodyTemplate: (r: IRowData) => {
        const isTotal = r.year === "First 10 Year Total";
        return (
          <span
            className={
              isTotal
                ? "inline-flex min-h-[2.75rem] w-full items-center py-2 font-semibold text-cyan-900"
                : "text-slate-700"
            }
          >
            {toCurrency(r.pc)}
          </span>
        );
      },
    },
  ];

  const onSubmit = async (values: ICalculatorForm) => {
    setError("");
    setResult(null);
    setShowBreakdown(false);

    const isAssistedType = values.type === "COMMERCIAL_SUITE" || values.type === "INDUSTRIAL_SUITE";

    if (!apiToken) {
      setError("Missing API token. Pass apiToken prop before submitting.");
      return;
    }

    if (isAssistedType) {
      setError("Please call 1300 990 612 for commercial and industrial properties.");
      return;
    }

    const cleanPrice = String(values.price || "").replace(/[^\d]/g, "");
    if (!cleanPrice) {
      setError("Please enter a valid purchase price.");
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        unm: apiToken,
        ResultType: "0",
        PropertyPrice: cleanPrice,
        City: values.city,
        BuildingCategory: mapCategory(values.type),
        BuildingQuality: values.finish,
        BuildYear: values.date,
        BudgetChange: values.pdate,
      });

      const response = await fetch(`${apiBaseUrl}?${params.toString()}`);
      const xmlText = await response.text();
      const xmlDoc = new DOMParser().parseFromString(xmlText, "text/xml");

      const errorCode = readXmlTag(xmlDoc, "ErrorCode");
      const errorDescription = readXmlTag(xmlDoc, "ErrorDescription");

      if (!response.ok || errorCode !== "0") {
        throw new Error(errorDescription || "No matching property data found for the selected criteria.");
      }

      const dvYears = parseYears(xmlDoc, "DV");
      const pcYears = parseYears(xmlDoc, "PC");
      const dvTotal = dvYears.reduce((sum, value) => sum + value, 0);
      const pcTotal = pcYears.reduce((sum, value) => sum + value, 0);

      setResult({
        degreeOfAccuracy: readXmlTag(xmlDoc, "DegreeOfAccuracy") || "",
        dvYears,
        pcYears,
        dvTotal,
        pcTotal,
        heroTotal: Math.max(dvTotal, pcTotal),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to fetch calculation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-50 via-white to-cyan-50 p-[1px] shadow-[0_24px_60px_-24px_rgba(15,23,42,0.45)]">
      <div className="pointer-events-none absolute -top-20 -right-24 h-64 w-64 rounded-full bg-cyan-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-sky-100/60 blur-3xl" />
      <REICard className="relative rounded-3xl border border-slate-200/80 bg-white/95 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
        <div className="p-6 sm:p-8">
          <div className="mb-8 flex flex-col gap-2.5 sm:mb-10">
            <span className="inline-flex w-fit items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-700">
              Washington Brown
            </span>
            <h2 className="text-2xl font-bold tracking-tight text-black sm:text-3xl">
              Depreciation Schedule Calculator
            </h2>
            <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
              Estimate your first 10-year depreciation claim in seconds with property-specific assumptions.
            </p>
          </div>

          <REITooltip
            targetId="wb-tooltip-purchase-price"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Enter the purchase amount in AUD exactly as shown in your Contract of Sale."
          />
          <REITooltip
            targetId="wb-tooltip-purchase-date"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Select the purchase timing so the estimate can apply the post-9 May 2017 depreciation rules correctly."
          />
          <REITooltip
            targetId="wb-tooltip-property-type"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Choose the property category that best matches your asset class (house, unit, highrise, commercial, or industrial)."
          />
          <REITooltip
            targetId="wb-tooltip-finish"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Select the standard of finish to reflect the level of fixtures, fit-out quality, and construction grade."
          />
          <REITooltip
            targetId="wb-tooltip-city"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Choose the nearest city so we can match your property against comparable data in that market."
          />
          <REITooltip
            targetId="wb-tooltip-build-year"
            position="top"
            customClass={styles["wb-calculator-tooltip"]}
            content="Enter the year construction started; if unknown, check council records or property documents."
          />
          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit, values }) => {
              const showDV = values.pdate !== "1";
              return (
                <>
                  <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <div className="rounded-xl border border-sky-200/70 bg-sky-50/50 p-3">
                        <label className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <span>Purchase Price</span>
                          <i className="pi pi-question-circle wb-tooltip-purchase-price cursor-pointer text-xs text-slate-500" />
                        </label>
                        <Field name="price">
                          {({ input }) => (
                            <div className="relative">
                              <i className="pi pi-home absolute left-3 top-1/2 -translate-y-1/2 text-sky-500" />
                              <REIInputText
                                id="wb-txt-price"
                                type="text"
                                name={input.name}
                                value={input.value}
                                onChange={(e) => input.onChange(e.target.value)}
                                placeholder="480000"
                                className="h-11 w-full rounded-xl border border-sky-200 bg-white pl-10 pr-3 py-2 text-slate-800 shadow-sm transition-all duration-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                              />
                            </div>
                          )}
                        </Field>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <span>Property Purchase Date</span>
                        <i className="pi pi-question-circle wb-tooltip-purchase-date cursor-pointer text-xs text-slate-500" />
                      </label>
                      <Field name="pdate">
                        {({ input }) => (
                          <REIDropDown
                            id="wb-drop-purchasedate"
                            value={input.value}
                            data={PURCHASE_DATE_OPTIONS}
                            label="label"
                            optionValue="value"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white shadow-sm"
                            onSelectionChange={(e: any) => input.onChange(e.value)}
                          />
                        )}
                      </Field>
                    </div>

                    <div>
                      <label className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <span>Property Type</span>
                        <i className="pi pi-question-circle wb-tooltip-property-type cursor-pointer text-xs text-slate-500" />
                      </label>
                      <Field name="type">
                        {({ input }) => (
                          <REIDropDown
                            id="wb-drop-propertytype"
                            value={input.value}
                            data={TYPE_OPTIONS}
                            label="label"
                            optionValue="value"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white shadow-sm"
                            onSelectionChange={(e: any) => input.onChange(e.value)}
                          />
                        )}
                      </Field>
                    </div>

                    <div>
                      <label className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <span>Standard of Finish</span>
                        <i className="pi pi-question-circle wb-tooltip-finish cursor-pointer text-xs text-slate-500" />
                      </label>
                      <Field name="finish">
                        {({ input }) => (
                          <REIDropDown
                            id="wb-drop-finish"
                            value={input.value}
                            data={FINISH_OPTIONS}
                            label="label"
                            optionValue="value"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white shadow-sm"
                            onSelectionChange={(e: any) => input.onChange(e.value)}
                          />
                        )}
                      </Field>
                    </div>

                    <div>
                      <label className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <span>Nearest City</span>
                        <i className="pi pi-question-circle wb-tooltip-city cursor-pointer text-xs text-slate-500" />
                      </label>
                      <Field name="city">
                        {({ input }) => (
                          <REIDropDown
                            id="wb-drop-city"
                            value={input.value}
                            data={CITY_OPTIONS}
                            label="label"
                            optionValue="value"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-white shadow-sm"
                            onSelectionChange={(e: any) => input.onChange(e.value)}
                          />
                        )}
                      </Field>
                    </div>

                    <Field name="date">
                      {({ input }) => (
                        <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
                          <div className="mb-4 flex items-center justify-between text-sm font-semibold text-slate-700">
                            <div className="flex items-center gap-2">
                              <span>Build Year</span>
                              <i className="pi pi-question-circle wb-tooltip-build-year cursor-pointer text-xs text-slate-500" />
                            </div>
                            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-900 shadow-sm">
                              {input.value}
                            </span>
                          </div>
                          <InputSlider
                            id="wb-sldr-buildyear"
                            value={Number(input.value)}
                            min={YEAR_MIN}
                            max={YEAR_MAX}
                            step={1}
                            classNames="w-full"
                            onChange={(e: any) => input.onChange(String(e?.value ?? e))}
                          />
                        </div>
                      )}
                    </Field>

                    <div className="md:col-span-2 pt-1">
                      <div className="rounded-2xl border border-[var(--primary-color)]/25 bg-gradient-to-r from-[var(--primary-color)]/5 via-white to-[var(--primary-color)]/5 p-5 shadow-sm">
                        <div className="flex flex-col gap-5 md:flex-row md:items-stretch md:justify-between">
                          <div className="flex w-full items-center justify-center md:w-auto md:justify-start">
                            <REIButton
                              id="wb-btn-calculate"
                              type="submit"
                              loading={loading}
                              label={loading ? "Calculating..." : "Calculate Estimate"}
                              classNames="w-full rounded-xl bg-[#238EA0] border-none px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 md:w-auto"
                              onClick={() => {}}
                            />
                          </div>

                          <REIDivider className="hidden md:block" layout="vertical" />

                          <div className="w-full text-center md:w-auto md:text-left md:pl-1">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              First 10 Year Total Claim
                            </p>
                            <p className="mt-1 text-3xl font-bold tracking-tight text-cyan-700">
                              {toCurrency(result?.heroTotal ?? 0)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">Run calculation to view the estimate.</p>
                          </div>
                        </div>

                        {result ? (
                          <div className="mt-5 flex justify-center">
                            <REIButton
                              id="wb-btn-breakdown"
                              type="button"
                              label={showBreakdown ? "Hide 10 Years Full Breakdown" : "Show 10 Years Full Breakdown"}
                              icon={showBreakdown ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                              iconPos="right"
                              classNames="w-full rounded-xl border border-[var(--primary-color)] bg-white px-5 py-2.5 text-sm font-semibold text-[var(--primary-color)] shadow-sm transition hover:bg-slate-50 md:w-auto"
                              onClick={() => setShowBreakdown((prev) => !prev)}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </form>

                  {error ? (
                    <div className="mt-6 rounded-xl border border-red-100 bg-red-50/80 px-4 py-3 shadow-sm">
                      <REIErrorMessage message={error} />
                    </div>
                  ) : null}

                  {result && showBreakdown ? (
                    <section className="mt-8 space-y-4">
                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <REIDataTable
                          data={rows}
                          columns={getTableColumns(showDV)}
                          customclass="text-sm"
                          rowClassName={totalRowClassName}
                          stripedRows
                        />
                      </div>
                    </section>
                  ) : null}
                </>
              );
            }}
          />
        </div>
      </REICard>
    </div>
  );
};

export default WashingtonBrownCalculator;
